/**
 * gf-drawer.js — Greenfield drawer + Vault multi-step drawer
 *
 * Owns:
 *  - Drawer state (gfDrawerSiteId, drawerMode, vaultDraft, etc.)
 *  - openGfDrawer (sole export consumed by script.js §GF-EVENT-WIRING)
 *  - openVaultStepDrawer, wireVaultDrawerEvents
 *  - wireGfDrawerCloseEvents
 *  - renderGfDrawerContent, renderGfDrawerList
 *  - renderDrawerListForWorkloadTypes, renderDrawerListForJobTypes
 *  - renderDrawerListForServerTypes, renderDrawerListForProtectedWorkloads
 *  - renderDrawerListForVault
 *  - refreshGfSiteChips
 *  - addComponentToSiteFromDrawer
 *  - addRepoMigrationRowToSite
 *  - CHIP_PLATFORM_FIELD (window-bridged for script.js §CORE-SITE-PIPELINE)
 *
 * Extracted from §GF-DRAWER + §VAULT-DRAWER in script.js (was lines 3949–5219).
 * Dead code removed: buildVdcVaultMigrationChip, buildVdcVaultMigrationSiteGroup,
 *   buildVdcVaultMigrationTableMarkup (chain was never called externally).
 * Drawer state vars removed from §GF-EVENT-WIRING header in script.js.
 * existingEnvironmentGlobalVbrDependentKeys / Vb365DependentKeys moved here
 *   (only used inside renderDrawerListForServerTypes).
 */

import { sites, existingEnvironmentFieldSchemaByKey, existingEnvironmentFieldLabels } from './state.js';
import {
    getSiteById,
    gfGroupLabel,
    getRepoMigrationWorkloads,
    getRepoMigrationPickerOptionLabel,
    isExistingEnvironmentFieldAvailableForEngagement,
    hasVbrAcrossAllSites,
} from './helpers.js';
import {
    GF_SVG_ICONS,
    getSiteDisplayName,
    getGfChipIcon,
    getCanvasCompItems,
    buildGfVaultChipMarkup,
    buildExistingEnvGroupMarkup,
    buildSectionedVeeamGroupContent,
    normalizeExistingEnvironment,
} from './recalc.js';
import {
    escapeHtml,
    normalizeEnvironmentCount,
    isProtectedTbWorkload,
    isRepoMigrationWorkload,
} from './utils.js';
import {
    workloadOptionGroups,
    jobTypeOptions,
    jobWorkloadOptions,
    sureBackupAllowedWorkloads,
    replicationAllowedWorkloads,
    cdpAllowedWorkloads,
    backupCopyExcludedWorkloads,
    pluginFullLabels,
    vb365RepoTypeOptions,
} from './workload-defs.js';
import { repoMigrationSourceStorageOptions } from './site-scope-picker.js';
import { openSourceWorkloadPane } from './source-workload-pane.js';
// unprotected-canvas.js import removed (Phase 6) — refreshGfSiteChips deleted, buildGfChipMarkup no longer needed here.
import { initGreenfieldSortables } from './greenfield-sortables.js';
import { addVeeamServerToSite, removeVeeamServerFromSite } from './veeam-server-architecture.js';
import { emit, BUS_EVENTS } from './bus.js';

// ── Local SVG constant ────────────────────────────────────────────────────────
// GF_SVG_DOC is also used in buildGfSummaryBarMarkup (script.js). Defined locally
// here to avoid a circular import; the script.js copy remains the canonical one.
const GF_SVG_DOC = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="3" y1="13" x2="21" y2="13"/></svg>`;

// ── Platform-field map ────────────────────────────────────────────────────────
/**
 * Maps EE chip keys that support a Windows/Linux platform toggle to their
 * corresponding site-level state field name.
 * Used here in renderDrawerListForServerTypes, and bridged to script.js for
 * buildExistingEnvChipMarkup (§CORE-SITE-PIPELINE).
 */
export const CHIP_PLATFORM_FIELD = {
    repositoryServers:  'existingEnvironmentRepoPlatform',
    vb365ProxyServers:  'existingEnvironmentVb365ProxyPlatform',
};
window.CHIP_PLATFORM_FIELD = CHIP_PLATFORM_FIELD;

/** Valid VB365 repository storage type values (mirrors vb365RepoTypeOptions values). */
const VALID_VB365_REPO_TYPES = new Set(vb365RepoTypeOptions.map((o) => o.value));

// ── Dependency-lock sets ──────────────────────────────────────────────────────
/** Keys that require at least one VBR server to exist across ANY site before they can be added. */
const existingEnvironmentGlobalVbrDependentKeys = new Set([
    'proxyServers', 'repositoryServers', 'wanAccelerators', 'virtualLab',
    'backupJobs', 'backupCopyJobs', 'sureBackupJobs', 'cdpPolicies', 'replicationJobs', 'enterprisePlugins'
]);

/** Keys that require at least one VB365 server to exist across ANY site before they can be added. */
const existingEnvironmentGlobalVb365DependentKeys = new Set([
    'vb365ProxyServers', 'vb365RepositoryServers'
]);

// ── Drawer state ──────────────────────────────────────────────────────────────
let gfDrawerSiteId = null;
let gfDrawerSearch = '';
const gfDrawerActiveFilters = new Set();
window.gfDrawerActiveFilters = gfDrawerActiveFilters;
let gfDrawerContextId = null;    // job ID or workload row ID when changing an existing row
let gfDrawerContextField = null; // 'jobType' | 'workloadType' — which field is being changed

let drawerMode = 'greenfield'; // 'greenfield' | 'existing-env' | 'vault' | 'unprotected-workload' | 'source-workload' | 'job' | 'job-workload' | 'job-new-workload'
let gfDrawerPendingJobType = null; // job type selected in step 1 of the new-job add flow
let drawerSourceVersion = 'v13';     // version toggle for VBR  in the existing-env drawer
let drawerVbrPlatform = 'windows';   // platform toggle for VBR in the existing-env drawer ('windows' | 'linux')
let drawerVbrExpanded = false;       // whether the VBR advanced options section is open
let drawerVbrVsaHa    = 'no';        // VSA HA selection for VBR add ('no' | 'yes') — Linux only
let drawerVroSourceVersion = 'v12';  // version toggle for VRO  in the existing-env drawer
let drawerVoneSourceVersion = 'v12'; // version toggle for VONE in the existing-env drawer

// Per-type version selection for non-VBR named server badge items.
// window-bridged so addVeeamServerToSite (veeam-server-architecture.js) can read the
// selected version when creating a new server instance.
const drawerServerVersions = {
    vbfaws: 'v10',
    vbfgc: 'v7',
    vbfaz:  'v8.1',
    vb365:  'v8',
    kasten: 'v8.5.8',
};
window.drawerServerVersions = drawerServerVersions;

// lockedQtyFieldKeys — defined and window-bridged in greenfield-sortables.js.

/** Component groups for the existing-environment (Upgrade / Health Check) drawer.
 *  Note: Veeam Agents are intentionally excluded — agent effort is derived
 *  automatically from physical workloads in jobs, not entered as a component. */
const existingEnvironmentDrawerGroups = [
    { label: 'VBR Backup Infrastructure', keys: ['proxyServers', 'repositoryServers', 'wanAccelerators', 'virtualLab'] },
    { label: 'VB365', keys: ['vb365ProxyServers', 'vb365RepositoryServers'] },
    { label: 'VBfCloud', keys: ['vbfCloudRepositoryServers'] }
];
window.existingEnvironmentDrawerGroups = existingEnvironmentDrawerGroups;

const EE_DRAWER_SVG_ICONS = {
    'VDP': `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="3" width="20" height="6" rx="1"/><rect x="2" y="15" width="20" height="6" rx="1"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>`,
    'VBR Backup Infrastructure': `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/><path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3"/></svg>`,
    'VB365': `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/></svg>`,
    'VBfCloud': `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/><path d="M12 13v4m-2-2h4" stroke-width="2" stroke-linecap="round"/></svg>`,
    'Containers': `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>`,
    'Jobs': `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M8 14l2 2 4-4"/></svg>`,
    'Agents': `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>`,
    'Plug-Ins': `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22v-5"/><path d="M9 8V2"/><path d="M15 8V2"/><path d="M18 8H6a1 1 0 0 0-1 1v5a7 7 0 0 0 14 0V9a1 1 0 0 0-1-1Z"/></svg>`
};

// Per-option icons for the Vault Migration source storage drawer.
const VAULT_DRAWER_ICONS = {
    'aws-s3': `<svg width="22" height="22" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect width="36" height="36" rx="5" fill="#FF9900"/><text x="18" y="16" text-anchor="middle" dominant-baseline="middle" font-size="12" font-weight="700" font-family="Arial,sans-serif" fill="white" letter-spacing="-0.3">AWS</text><path d="M7 27Q18 33 29 27" stroke="white" stroke-width="2.8" fill="none" stroke-linecap="round"/><path d="M24 23.5L29 27 24 30.5" stroke="white" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
    'azure-blob': `<svg width="20" height="20" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M6.5 1L.5 14h5l3-8 3.5 8h5L9 1z" fill="#0078D4"/><path d="M.5 14l5.5-1.2 2.8 3.2z" fill="#50E6FF"/><path d="M9 1l3.5 12.8h4.5z" fill="#0050D0"/></svg>`,
    'onprem-block': `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="7" width="20" height="13" rx="2"/><circle cx="12" cy="13.5" r="3.25"/><circle cx="12" cy="13.5" r="1.1" fill="currentColor" stroke="none"/><path d="M19 10h.01" stroke-width="2.5"/><path d="M2 11h4" stroke-width="1.4" opacity="0.5"/></svg>`,
    'onprem-s3': `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="2" width="20" height="5.5" rx="1.5"/><rect x="2" y="9.25" width="20" height="5.5" rx="1.5"/><rect x="2" y="16.5" width="20" height="5.5" rx="1.5"/><line x1="6" y1="4.75" x2="6.01" y2="4.75"/><line x1="6" y1="12" x2="6.01" y2="12"/><line x1="6" y1="19.25" x2="6.01" y2="19.25"/></svg>`,
    'smb-nfs': `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6z"/><path d="M3 6l2-3h14l2 3"/><line x1="12" y1="6" x2="12" y2="21"/><line x1="3" y1="13.5" x2="21" y2="13.5"/></svg>`,
    'cloud-s3': `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/><line x1="9" y1="15" x2="9.01" y2="15" stroke-width="2.5"/><line x1="12" y1="15" x2="12.01" y2="15" stroke-width="2.5"/><line x1="15" y1="15" x2="15.01" y2="15" stroke-width="2.5"/></svg>`,
    'dedup-appliance': `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#b45309" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><ellipse cx="12" cy="5" rx="8" ry="2.5"/><path d="M4 5v5c0 1.38 3.58 2.5 8 2.5s8-1.12 8-2.5V5"/><path d="M4 10v5c0 1.38 3.58 2.5 8 2.5s8-1.12 8-2.5v-5"/><path d="M9 19.5l3 2 3-2" stroke-width="1.4"/><line x1="12" y1="17.5" x2="12" y2="21.5" stroke-width="1.4"/></svg>`,
    'default': `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5a4 4 0 0 1 8 0v2"/><circle cx="12" cy="13" r="2"/><line x1="12" y1="15" x2="12" y2="17"/></svg>`
};
window.VAULT_DRAWER_ICONS = VAULT_DRAWER_ICONS;

// ── Drawer close helpers (exported — called from script.js and internally) ────

export function closeGfDrawer() {
    const drawerEl = document.getElementById('gf-drawer');
    const overlayEl = document.getElementById('gf-drawer-overlay');
    if (drawerEl) {
        drawerEl.classList.remove('open');
        setTimeout(() => { if (!drawerEl.classList.contains('open')) drawerEl.hidden = true; }, 260);
    }
    if (overlayEl) { overlayEl.classList.remove('open'); overlayEl.setAttribute('aria-hidden', 'true'); }
    gfDrawerSiteId = null;
}

export function closeVaultStepDrawer() {
    const drawerEl = document.getElementById('vault-step-drawer');
    const overlayEl = document.getElementById('vault-drawer-overlay');
    if (drawerEl) {
        drawerEl.classList.remove('open');
        setTimeout(() => { if (!drawerEl.classList.contains('open')) drawerEl.hidden = true; }, 260);
    }
    if (overlayEl) { overlayEl.classList.remove('open'); overlayEl.setAttribute('aria-hidden', 'true'); }
    vaultStepDrawerSiteId = null;
    vaultChangeRowId = null;
}

export function renderGfDrawerFacets() {
    const facetsEl = document.getElementById('gf-drawer-facets');
    if (!facetsEl) return;

    // Step 2 of new-job flow: show a back breadcrumb button instead of facets
    if (drawerMode === 'job-new-workload') {
        const jobDef = jobTypeOptions.find((t) => t.value === gfDrawerPendingJobType);
        const jobLabel = jobDef ? jobDef.label : 'Job Type';
        facetsEl.innerHTML = `<button type="button" class="gf-drawer-back-btn" data-gf-drawer-back="job">← ${escapeHtml(jobLabel)}</button>`;
        return;
    }

    // No facets for vault, job/workload pickers, or dedicated protected picker
    if (drawerMode === 'vault' || drawerMode === 'protected'
        || drawerMode === 'job' || drawerMode === 'job-workload') {
        facetsEl.innerHTML = '';
        return;
    }

    // Unprotected workload drawer — facets derived from jobWorkloadOptions groups
    if (drawerMode === 'unprotected-workload') {
        const seen = new Set();
        const groups = [];
        jobWorkloadOptions.forEach((w) => {
            if (!seen.has(w.group)) { seen.add(w.group); groups.push(w.group); }
        });
        facetsEl.innerHTML = groups.map((g) => {
            const isActive = gfDrawerActiveFilters.has(g);
            return `<button type="button" class="gf-facet-chip${isActive ? ' active' : ''}" data-gf-facet="${escapeHtml(g)}">${escapeHtml(g)}</button>`;
        }).join('');
        return;
    }

    // Combined component drawer — facets include "VDP" + infrastructure groups + Containers
    if (drawerMode === 'existing-env') {
        const groups = ['VDP', ...existingEnvironmentDrawerGroups.map((g) => g.label), 'Containers'];
        facetsEl.innerHTML = groups.map((g) => {
            const isActive = gfDrawerActiveFilters.has(g);
            return `<button type="button" class="gf-facet-chip${isActive ? ' active' : ''}" data-gf-facet="${escapeHtml(g)}">${escapeHtml(g)}</button>`;
        }).join('');
        return;
    }

    const groups = workloadOptionGroups.map((g) => g.label);
    facetsEl.innerHTML = groups.map((g) => {
        const isActive = gfDrawerActiveFilters.has(g);
        return `<button type="button" class="gf-facet-chip${isActive ? ' active' : ''}" data-gf-facet="${escapeHtml(g)}">${escapeHtml(g)}</button>`;
    }).join('');
}

// ── Vault multi-step drawer ───────────────────────────────────────────────────

let vaultStepDrawerSiteId = null;
let vaultDraft = { source: '', repo: '' };
// 'component' = opened from + Add Component → Repository
// 'migration' = opened from standalone vault migration path (legacy, kept for safety)
// 'change'    = opened from clicking the source-type badge on an existing VBR repo chip
let vaultStepDrawerOrigin = 'component';
// rowId of the workload entry being edited in 'change' mode (null otherwise)
let vaultChangeRowId = null;

export function openVaultStepDrawer(siteId, origin = 'component', changeRowId = null) {
    vaultStepDrawerOrigin = origin;
    vaultStepDrawerSiteId = siteId;
    vaultChangeRowId = changeRowId;

    const site = getSiteById(siteId);
    const siteIndex = site ? sites.indexOf(site) : -1;
    const siteName = site ? getSiteDisplayName(site, siteIndex) : '';

    const subtitleEl = document.getElementById('vault-drawer-subtitle');

    // In 'change' mode: pre-populate draft from the existing workload entry
    if (origin === 'change' && changeRowId !== null && site) {
        const existing = Array.isArray(site.workloads)
            ? site.workloads.find((w) => w.rowId === changeRowId)
            : null;
        vaultDraft = {
            source: existing?.vdcVaultSourceStorageType || '',
            repo:   existing?.vdcVaultRepositoryType   || '',
        };
        if (subtitleEl) subtitleEl.textContent = siteName ? `Editing: ${siteName}` : '';
    } else {
        vaultDraft = { source: '', repo: '' };
        if (subtitleEl) subtitleEl.textContent = siteName ? `Adding to: ${siteName}` : '';
    }

    // Reset all tile/option selections
    document.querySelectorAll('[data-vault-src]').forEach((t) => t.classList.remove('vault-src-tile--selected'));
    document.querySelectorAll('[data-vault-repo]').forEach((r) => r.classList.remove('vault-opt-row--selected', 'vault-src-tile--selected'));

    if (origin === 'change') {
        // Pre-select both current tiles and unghost step 1 immediately
        document.getElementById('vault-step-1')?.classList.remove('vault-step-panel--ghosted');
        if (vaultDraft.source) {
            document.querySelector(`[data-vault-src="${CSS.escape(vaultDraft.source)}"]`)?.classList.add('vault-src-tile--selected');
        }
        if (vaultDraft.repo) {
            document.querySelector(`[data-vault-repo="${CSS.escape(vaultDraft.repo)}"]`)?.classList.add('vault-src-tile--selected');
            if (vaultDraft.repo === 'sobr-capacity-tier') {
                document.querySelector('[data-vault-repo="sobr-performance-tier"]')?.classList.add('vault-src-tile--selected');
            }
        }
        // Apply SOBR Cap Tier ghost rule for current source
        const sobrCapTile = document.querySelector('[data-vault-repo="sobr-capacity-tier"]');
        if (vaultDraft.source === 'onprem-block' || vaultDraft.source === 'dedup-appliance' || vaultDraft.source === 'smb-nfs') {
            sobrCapTile?.classList.add('vault-src-tile--ghosted');
        } else {
            sobrCapTile?.classList.remove('vault-src-tile--ghosted');
        }
        // Do NOT call updateVaultDrawerButton here — draft is already complete and calling it
        // would immediately auto-confirm before the user makes any change.
    } else {
        // Ghost step 1 until step 0 is answered
        document.getElementById('vault-step-1')?.classList.add('vault-step-panel--ghosted');
        // Reset SOBR Cap Tier ghost state
        document.querySelector('[data-vault-repo="sobr-capacity-tier"]')?.classList.remove('vault-src-tile--ghosted');
        updateVaultDrawerButton();
    }

    const drawerEl = document.getElementById('vault-step-drawer');
    const overlayEl = document.getElementById('vault-drawer-overlay');
    if (!drawerEl) return;

    drawerEl.hidden = false;
    drawerEl.classList.add('open');
    if (overlayEl) { overlayEl.classList.add('open'); overlayEl.removeAttribute('aria-hidden'); }

    wireVaultDrawerEvents();
}

function updateVaultDrawerButton() {
    if (vaultDraft.source && vaultDraft.repo) {
        confirmVaultDrawerEntry();
    }
}

function confirmVaultDrawerEntry() {
    if (vaultStepDrawerSiteId === null) return;

    if (vaultStepDrawerOrigin === 'change' && vaultChangeRowId !== null) {
        // Update-in-place: find the existing workload entry and patch source/repo fields
        const site = getSiteById(vaultStepDrawerSiteId);
        const entry = site && Array.isArray(site.workloads)
            ? site.workloads.find((w) => w.rowId === vaultChangeRowId)
            : null;
        if (entry) {
            entry.vdcVaultSourceStorageType = vaultDraft.source;
            entry.vdcVaultRepositoryType    = vaultDraft.repo;
        }
        closeVaultStepDrawer();
        emit(BUS_EVENTS.SITE_LIST_CHANGED);
        return;
    }

    const isRepositoryComponent = vaultStepDrawerOrigin === 'component';
    const siteIdToReopen = isRepositoryComponent ? vaultStepDrawerSiteId : null;
    addRepoMigrationRowToSite(vaultStepDrawerSiteId, vaultDraft.source, vaultDraft.repo, '', isRepositoryComponent);
    closeVaultStepDrawer();
    if (siteIdToReopen !== null) {
        openGfDrawer(siteIdToReopen, 'existing-env');
    }
}

let vaultDrawerEventsWired = false;

function wireVaultDrawerEvents() {
    if (vaultDrawerEventsWired) return;
    vaultDrawerEventsWired = true;

    const overlayEl = document.getElementById('vault-drawer-overlay');

    if (overlayEl) overlayEl.addEventListener('click', closeVaultStepDrawer);
    document.getElementById('vault-drawer-close')?.addEventListener('click', closeVaultStepDrawer);
    document.getElementById('vault-drawer-cancel')?.addEventListener('click', () => {
        const siteId = vaultStepDrawerSiteId;
        const origin = vaultStepDrawerOrigin;
        closeVaultStepDrawer();
        // Only reopen the EE drawer when coming from an add flow; 'change' mode was opened
        // from a chip badge on the canvas so there's nothing to return to.
        if (siteId !== null && origin !== 'change') openGfDrawer(siteId, 'existing-env');
    });

    // Source tile clicks
    document.getElementById('vault-step-0')?.addEventListener('click', (e) => {
        const tile = e.target.closest('[data-vault-src]');
        if (!tile) return;
        vaultDraft.source = tile.dataset.vaultSrc;
        document.querySelectorAll('[data-vault-src]').forEach((t) => t.classList.remove('vault-src-tile--selected'));
        tile.classList.add('vault-src-tile--selected');

        // Unghost step 1 now that step 0 is answered
        document.getElementById('vault-step-1')?.classList.remove('vault-step-panel--ghosted');

        // Ghost SOBR Cap Tier if Block Storage, Dedup Appliance, or SMB/NFS is selected
        const sobrCapTile = document.querySelector('[data-vault-repo="sobr-capacity-tier"]');
        if (vaultDraft.source === 'onprem-block' || vaultDraft.source === 'dedup-appliance' || vaultDraft.source === 'smb-nfs') {
            sobrCapTile?.classList.add('vault-src-tile--ghosted');
            if (vaultDraft.repo === 'sobr-capacity-tier') {
                vaultDraft.repo = '';
                sobrCapTile?.classList.remove('vault-src-tile--selected');
            }
        } else {
            sobrCapTile?.classList.remove('vault-src-tile--ghosted');
        }

        updateVaultDrawerButton();
    });

    // Repo tile clicks
    document.getElementById('vault-step-1')?.addEventListener('click', (e) => {
        const tile = e.target.closest('[data-vault-repo]');
        if (!tile) return;
        vaultDraft.repo = tile.dataset.vaultRepo;
        document.querySelectorAll('[data-vault-repo]').forEach((t) => t.classList.remove('vault-src-tile--selected'));
        tile.classList.add('vault-src-tile--selected');
        // Cap Tier requires Perf Tier — auto-highlight it to signal the dependency
        if (vaultDraft.repo === 'sobr-capacity-tier') {
            document.querySelector('[data-vault-repo="sobr-performance-tier"]')?.classList.add('vault-src-tile--selected');
        }
        updateVaultDrawerButton();
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && vaultStepDrawerSiteId !== null) closeVaultStepDrawer();
    });
}

// pendingProxyOsType — tracks the OS toggle state in the main drawer.
// Updated by the inline Win/Linux radio in the proxy row; read when adding a proxy directly.
let pendingProxyOsType = 'windows';

// ── VB365 repository storage type step drawer ────────────────────────────────

let vb365RepoStepDrawerSiteId = null;
let vb365RepoDrawerMode = 'add'; // 'add' | 'change'
let vb365RepoDraft = { repoType: '' };
let vb365RepoDrawerEventsWired = false;
let vb365RepoEditSlot = null; // slot index being edited in 'change' mode

/**
 * Opens the VB365 repository storage type step drawer.
 * mode='add'    → confirms by incrementing vb365RepositoryServers count on the site and
 *                 writing type/TB into the new slot's per-slot dicts.
 * mode='change' → confirms by updating the given slotIndex in the per-slot dicts without
 *                 adding a new slot.
 * @param {number} siteId
 * @param {'add'|'change'} mode
 * @param {number|null} slotIndex  Required when mode='change'.
 */
export function openVb365RepoStepDrawer(siteId, mode = 'add', slotIndex = null) {
    vb365RepoStepDrawerSiteId = siteId;
    vb365RepoDrawerMode = mode;
    vb365RepoEditSlot = slotIndex;

    const site = getSiteById(siteId);
    const siteIndex = site ? sites.indexOf(site) : -1;
    const siteName = site ? getSiteDisplayName(site, siteIndex) : '';

    // Pre-populate draft from per-slot dicts (change mode) or defaults (add mode)
    let currentType = 'block';
    if (mode === 'change' && slotIndex !== null && site) {
        const repoTypes = site.existingEnvironmentVb365RepoTypes || {};
        currentType = repoTypes[slotIndex] || 'block';
    }
    vb365RepoDraft = { repoType: currentType };

    const subtitleEl = document.getElementById('vb365-repo-drawer-subtitle');
    if (subtitleEl) subtitleEl.textContent = siteName ? `${mode === 'change' ? 'Editing' : 'Adding to'}: ${siteName}` : '';

    const titleEl = document.getElementById('vb365-repo-drawer-title');
    if (titleEl) titleEl.textContent = mode === 'change' ? 'Edit repository details' : 'Repository details';

    // Pre-select the current storage type tile
    document.querySelectorAll('[data-vb365-repo-type]').forEach((t) => {
        t.classList.toggle('vault-src-tile--selected', t.dataset.vb365RepoType === currentType);
    });

    const drawerEl = document.getElementById('vb365-repo-step-drawer');
    const overlayEl = document.getElementById('vb365-repo-drawer-overlay');
    if (!drawerEl) return;

    drawerEl.hidden = false;
    drawerEl.classList.add('open');
    if (overlayEl) { overlayEl.classList.add('open'); overlayEl.removeAttribute('aria-hidden'); }

    wireVb365RepoDrawerEvents();
}

export function closeVb365RepoStepDrawer() {
    const drawerEl = document.getElementById('vb365-repo-step-drawer');
    const overlayEl = document.getElementById('vb365-repo-drawer-overlay');
    if (drawerEl) {
        drawerEl.classList.remove('open');
        setTimeout(() => { if (!drawerEl.classList.contains('open')) drawerEl.hidden = true; }, 260);
    }
    if (overlayEl) { overlayEl.classList.remove('open'); overlayEl.setAttribute('aria-hidden', 'true'); }
    vb365RepoStepDrawerSiteId = null;
    vb365RepoEditSlot = null;
}

function confirmVb365RepoDrawerEntry() {
    if (vb365RepoStepDrawerSiteId === null || !vb365RepoDraft.repoType) return;
    const site = getSiteById(vb365RepoStepDrawerSiteId);
    if (!site) { closeVb365RepoStepDrawer(); return; }

    // Ensure per-slot type dict exists
    if (typeof site.existingEnvironmentVb365RepoTypes !== 'object' || site.existingEnvironmentVb365RepoTypes === null) {
        site.existingEnvironmentVb365RepoTypes = {};
    }

    if (vb365RepoDrawerMode === 'add') {
        // Increment the repository count and write storage type into the new slot
        if (!site.existingEnvironment || typeof site.existingEnvironment !== 'object') {
            site.existingEnvironment = normalizeExistingEnvironment({});
        }
        const current = normalizeEnvironmentCount(site.existingEnvironment.vb365RepositoryServers);
        const newSlot = current; // slots are 0-based; new slot index = old count
        site.existingEnvironment.vb365RepositoryServers = current + 1;
        if (current === 0) {
            if (!Array.isArray(site.existingEnvironmentDisplayOrder)) site.existingEnvironmentDisplayOrder = [];
            if (!site.existingEnvironmentDisplayOrder.includes('vb365RepositoryServers')) {
                site.existingEnvironmentDisplayOrder.push('vb365RepositoryServers');
            }
        }
        site.existingEnvironmentVb365RepoTypes[newSlot] = vb365RepoDraft.repoType;
        // Initialise consumed TB to the default 50 TB so the chip shows a value immediately.
        // This must be explicit — relying on the ?? 50 fallback in recalc means the value
        // is undefined in state, and the first user interaction writes 0 (clearing the field).
        if (typeof site.existingEnvironmentVb365RepoConsumedTbs !== 'object' || site.existingEnvironmentVb365RepoConsumedTbs === null) {
            site.existingEnvironmentVb365RepoConsumedTbs = {};
        }
        if (site.existingEnvironmentVb365RepoConsumedTbs[newSlot] === undefined) {
            site.existingEnvironmentVb365RepoConsumedTbs[newSlot] = 50;
        }
    } else if (vb365RepoDrawerMode === 'change' && vb365RepoEditSlot !== null) {
        // Update the specific slot's type only — count and consumed TB stay the same
        site.existingEnvironmentVb365RepoTypes[vb365RepoEditSlot] = vb365RepoDraft.repoType;
    }

    const siteIdToReopen = vb365RepoDrawerMode === 'add' ? vb365RepoStepDrawerSiteId : null;
    closeVb365RepoStepDrawer();
    emit(BUS_EVENTS.SITE_LIST_CHANGED);
    window.updateTotalInstances?.();
    window.updateWorkloadValidationState?.();
    window.updateGfSummaryCount?.();
    window.syncDesignOptionFields?.();
    if (siteIdToReopen !== null) {
        openGfDrawer(siteIdToReopen, 'existing-env');
    }
}

function wireVb365RepoDrawerEvents() {
    if (vb365RepoDrawerEventsWired) return;
    vb365RepoDrawerEventsWired = true;

    const overlayEl = document.getElementById('vb365-repo-drawer-overlay');
    if (overlayEl) overlayEl.addEventListener('click', closeVb365RepoStepDrawer);
    document.getElementById('vb365-repo-drawer-close')?.addEventListener('click', closeVb365RepoStepDrawer);
    document.getElementById('vb365-repo-drawer-cancel')?.addEventListener('click', () => {
        const siteId = vb365RepoStepDrawerSiteId;
        closeVb365RepoStepDrawer();
        if (siteId !== null) openGfDrawer(siteId, 'existing-env');
    });

    // Storage type tile clicks — immediately confirm on selection
    document.getElementById('vb365-repo-step-0')?.addEventListener('click', (e) => {
        const tile = e.target.closest('[data-vb365-repo-type]');
        if (!tile) return;
        if (!VALID_VB365_REPO_TYPES.has(tile.dataset.vb365RepoType)) return;
        vb365RepoDraft.repoType = tile.dataset.vb365RepoType;
        confirmVb365RepoDrawerEntry();
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && vb365RepoStepDrawerSiteId !== null) closeVb365RepoStepDrawer();
    });
}

// ── Greenfield drawer ─────────────────────────────────────────────────────────

export function openGfDrawer(siteId, mode = 'greenfield', context = {}) {
    // Vault migration uses its own multi-step drawer
    if (mode === 'vault') {
        openVaultStepDrawer(siteId);
        return;
    }

    drawerMode = mode;
    gfDrawerSiteId = siteId;
    gfDrawerSearch = '';
    gfDrawerActiveFilters.clear();
    gfDrawerContextId    = context.contextId    ?? null;
    gfDrawerContextField = context.contextField ?? null;

    const drawerEl = document.getElementById('gf-drawer');
    const overlayEl = document.getElementById('gf-drawer-overlay');
    const searchEl = document.getElementById('gf-drawer-search');
    const titleEl = document.getElementById('gf-drawer-title');
    const subtitleEl = document.getElementById('gf-drawer-subtitle');

    if (!drawerEl) return;

    const site = getSiteById(siteId);
    const siteIndex = sites.indexOf(site);
    const siteName = site ? getSiteDisplayName(site, siteIndex) : '';

    if (titleEl) {
        titleEl.textContent = mode === 'vault'                 ? 'Add Vault Migration'
            : mode === 'existing-env'                          ? 'Add Veeam Component'
            : mode === 'unprotected-workload'                  ? (gfDrawerContextId !== null ? 'Change Workload Type' : 'Add Currently Unprotected Workload')
            : mode === 'source-workload'                       ? 'Add Source Workload'
            : mode === 'job'                                   ? (gfDrawerContextId !== null ? 'Change Job Type' : 'Add Veeam Job')
            : mode === 'job-workload'                          ? 'Change Workload Type'
            : 'Add Workload';
    }
    if (subtitleEl) subtitleEl.textContent = siteName ? `Site: ${siteName}` : '';
    if (searchEl) searchEl.value = '';

    renderGfDrawerContent();

    drawerEl.hidden = false;
    drawerEl.classList.add('open');
    if (overlayEl) { overlayEl.classList.add('open'); overlayEl.removeAttribute('aria-hidden'); }

    setTimeout(() => searchEl?.focus(), 50);
    wireGfDrawerCloseEvents();
}

let gfDrawerEventsWired = false;

function wireGfDrawerCloseEvents() {
    if (gfDrawerEventsWired) return;
    gfDrawerEventsWired = true;

    const overlayEl = document.getElementById('gf-drawer-overlay');
    const closeBtn = document.getElementById('gf-drawer-close');
    const searchEl = document.getElementById('gf-drawer-search');
    const facetsEl = document.getElementById('gf-drawer-facets');
    const listEl = document.getElementById('gf-drawer-list');

    if (overlayEl) overlayEl.addEventListener('click', closeGfDrawer);
    if (closeBtn) closeBtn.addEventListener('click', closeGfDrawer);

    // Escape key to close
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && gfDrawerSiteId !== null) closeGfDrawer();
    });

    // Search input
    if (searchEl) {
        searchEl.addEventListener('input', () => {
            gfDrawerSearch = searchEl.value.trim().toLowerCase();
            renderGfDrawerContent();
        });
    }

    // Facet chips (event delegation)
    if (facetsEl) {
        facetsEl.addEventListener('click', (e) => {
            // Back button shown in step 2 of the new-job add flow
            if (e.target.closest('[data-gf-drawer-back]')) {
                gfDrawerPendingJobType = null;
                drawerMode = 'job';
                const titleEl = document.getElementById('gf-drawer-title');
                if (titleEl) titleEl.textContent = 'Add Veeam Job';
                const searchEl2 = document.getElementById('gf-drawer-search');
                if (searchEl2) { searchEl2.placeholder = 'Search workloads…'; searchEl2.value = ''; }
                gfDrawerSearch = '';
                renderGfDrawerContent();
                return;
            }
            const chip = e.target.closest('[data-gf-facet]');
            if (!chip) return;
            const val = chip.dataset.gfFacet;
            if (gfDrawerActiveFilters.has(val)) {
                gfDrawerActiveFilters.delete(val);
                chip.classList.remove('active');
            } else {
                gfDrawerActiveFilters.add(val);
                chip.classList.add('active');
            }
            renderGfDrawerList();
        });
    }

    // List item clicks
    if (listEl) {
        // Version toggles live inside list items — handle change here so
        // selecting a version doesn't also trigger "add component"
        listEl.addEventListener('change', (e) => {
            const input = e.target.closest('input[name="drawer-source-version"]');
            if (input) {
                drawerSourceVersion = input.value;
                // v11/v12 are Windows-only — reset platform and collapse VSA HA
                if (drawerSourceVersion !== 'v13') {
                    drawerVbrPlatform  = 'windows';
                    drawerVbrVsaHa     = 'no';
                    drawerVbrExpanded  = false;
                }
                renderGfDrawerList();
                return;
            }
            const vbrPlatInput = e.target.closest('input[name="drawer-vbr-platform"]');
            if (vbrPlatInput) {
                drawerVbrPlatform = vbrPlatInput.value;
                if (drawerVbrPlatform === 'windows') { drawerVbrVsaHa = 'no'; drawerVbrExpanded = false; }
                renderGfDrawerList();
                return;
            }
            const vbrVsaHaInput = e.target.closest('input[name="drawer-vbr-vsa-ha"]');
            if (vbrVsaHaInput) { drawerVbrVsaHa = vbrVsaHaInput.value; return; }
            // Proxy OS toggle in drawer 1 — updates module-level pendingProxyOsType (not per-site state)
            const proxyOsInput = e.target.closest('input[data-gf-proxy-os-toggle]');
            if (proxyOsInput) {
                pendingProxyOsType = proxyOsInput.value;
                return;
            }
            // Repository / VB365 proxy platform toggles — update the site's platform state directly
            const compPlatformInput = e.target.closest('input[data-ee-drawer-platform]');
            if (compPlatformInput) {
                const siteId = parseInt(compPlatformInput.dataset.siteId, 10);
                const field  = compPlatformInput.dataset.eeDrawerPlatform;
                const value  = compPlatformInput.value;
                const _site  = getSiteById(siteId);
                if (_site && (value === 'windows' || value === 'linux') && field in _site) {
                    _site[field] = value;
                }
                return;
            }
            const vroVerInput = e.target.closest('input[name="drawer-vro-source-version"]');
            if (vroVerInput) { drawerVroSourceVersion = vroVerInput.value; return; }
            const voneVerInput = e.target.closest('input[name="drawer-vone-source-version"]');
            if (voneVerInput) { drawerVoneSourceVersion = voneVerInput.value; return; }
            // Badge type version toggles use name="drawer-server-version-{typeKey}"
            const badgeInput = e.target.closest('input[name^="drawer-server-version-"]');
            if (badgeInput) {
                const typeKey = badgeInput.closest('[data-gf-version-type]')?.dataset.gfVersionType;
                if (typeKey && Object.hasOwn(drawerServerVersions, typeKey)) {
                    drawerServerVersions[typeKey] = badgeInput.value;
                }
            }
        });

        listEl.addEventListener('click', (e) => {
            // Ignore clicks that land inside the version/platform toggle
            if (e.target.closest('[data-gf-version-toggle]')) return;

            // Expand/collapse button for VBR advanced options (VSA HA)
            if (e.target.closest('[data-gf-vbr-expand-toggle]')) {
                drawerVbrExpanded = !drawerVbrExpanded;
                renderGfDrawerList();
                return;
            }

            // Minus control on an added drawer item — decrement / remove one instance.
            const removeBtn = e.target.closest('[data-gf-remove-wl]');
            if (removeBtn) {
                e.stopPropagation();
                const removeValue = removeBtn.dataset.gfRemoveWl;
                if (!removeValue || gfDrawerSiteId === null) return;
                const rmSite = getSiteById(gfDrawerSiteId);
                if (removeBtn.dataset.gfRemoveServer) {
                    // Named server instance — remove the most-recently-added of this type.
                    const ofType = Array.isArray(rmSite?.veeamServers)
                        ? rmSite.veeamServers.filter((s) => s.type === removeValue)
                        : [];
                    const last = ofType[ofType.length - 1];
                    if (last) removeVeeamServerFromSite(gfDrawerSiteId, last.instanceId);
                } else {
                    // Count chip — resolve the proxy virtual key to its platform-specific field.
                    let removeField = removeValue;
                    if (removeValue === 'proxyServers') {
                        const preferred = pendingProxyOsType === 'linux' ? 'proxyServersLinux' : 'proxyServersWindows';
                        const other     = pendingProxyOsType === 'linux' ? 'proxyServersWindows' : 'proxyServersLinux';
                        removeField = normalizeEnvironmentCount(rmSite?.existingEnvironment?.[preferred]) > 0 ? preferred : other;
                    }
                    window.removeLastComponentSlotFromSite(gfDrawerSiteId, removeField);
                }
                renderGfDrawerList();
                return;
            }

            const item = e.target.closest('[data-gf-add-wl]');
            if (!item) return;
            const value = item.dataset.gfAddWl;
            if (!value || gfDrawerSiteId === null) return;

            if (drawerMode === 'existing-env') {
                // Two sub-types within the combined drawer:
                // data-gf-add-server → named server instance card
                // data-gf-add-comp   → per-site infrastructure/jobs chip
                if (item.dataset.gfAddServer) {
                    addVeeamServerToSite(gfDrawerSiteId, value);
                } else {
                    if (item.classList.contains('is-locked')) return;
                    addComponentToSiteFromDrawer(gfDrawerSiteId, value);
                    // VSA HA: add a second VBR slot for the HA pair (Linux only)
                    if (value === 'vbrServers' && drawerVbrVsaHa === 'yes') {
                        addComponentToSiteFromDrawer(gfDrawerSiteId, value);
                        // Tag the two new slots as primary / secondary so they render as separate draggable chips.
                        const _haSite = getSiteById(gfDrawerSiteId);
                        if (_haSite) {
                            const _haTotal = normalizeEnvironmentCount(_haSite.existingEnvironment?.vbrServers);
                            if (!_haSite.existingEnvironmentVbrHaRoles || typeof _haSite.existingEnvironmentVbrHaRoles !== 'object') {
                                _haSite.existingEnvironmentVbrHaRoles = {};
                            }
                            _haSite.existingEnvironmentVbrHaRoles[_haTotal - 2] = 'primary';
                            _haSite.existingEnvironmentVbrHaRoles[_haTotal - 1] = 'secondary';
                            if (!_haSite.existingEnvironmentVbrHaPairIds || typeof _haSite.existingEnvironmentVbrHaPairIds !== 'object') {
                                _haSite.existingEnvironmentVbrHaPairIds = {};
                            }
                            const _haPairId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : (Math.random().toString(36).slice(2) + Date.now().toString(36));
                            _haSite.existingEnvironmentVbrHaPairIds[_haTotal - 2] = _haPairId;
                            _haSite.existingEnvironmentVbrHaPairIds[_haTotal - 1] = _haPairId;
                            emit(BUS_EVENTS.SITE_LIST_CHANGED); // re-render now haRoles and pairIds are in place
                        }
                    }
                }
                renderGfDrawerList();
                return;
            }

            if (drawerMode === 'protected') {
                const site = getSiteById(gfDrawerSiteId);
                if (!site) return;
                if (!Array.isArray(site.protectedWorkloads)) site.protectedWorkloads = [];
                const nextEntry = { ...window.buildDefaultSelectedWorkloadEntry(value) };
                site.protectedWorkloads.push(nextEntry);
                renderGfDrawerList();
                emit(BUS_EVENTS.SITE_LIST_CHANGED);
                return;
            }

            // [Phase 6] 'unprotected-workload' drawer mode removed — replaced by 'source-workload'.

            // Source workload picker — add new source workload entry, then jump straight
            // to the config pane for that workload so the user can set counts + jobs immediately.
            if (drawerMode === 'source-workload') {
                const siteIdForPane = gfDrawerSiteId;
                const entry = window.addSourceWorkloadToSite(siteIdForPane, value);
                closeGfDrawer();
                if (entry) openSourceWorkloadPane(siteIdForPane, entry.id);
                return;
            }

            // [Phase 6] 'job', 'job-new-workload', 'job-workload' drawer modes removed.
            // Jobs replaced by source workloads (source-workload-pane.js).

            if (drawerMode === 'vault') {
                addRepoMigrationRowToSite(gfDrawerSiteId, value);
                renderGfDrawerList();
                return;
            }

            // Greenfield mode — add to uncategorised staging group; user drags to correct group
            const site = getSiteById(gfDrawerSiteId);
            if (!site) return;
            if (!Array.isArray(site.uncategorisedWorkloads)) site.uncategorisedWorkloads = [];
            site.uncategorisedWorkloads.push(window.buildDefaultSelectedWorkloadEntry(value));
            renderGfDrawerList();
            emit(BUS_EVENTS.SITE_LIST_CHANGED);
        });
    }
}

function renderGfDrawerContent() {
    renderGfDrawerFacets();
    renderGfDrawerList();
}

function renderGfDrawerList() {
    if (drawerMode === 'existing-env') { renderDrawerListForServerTypes(); return; }
    if (drawerMode === 'vault') { renderDrawerListForVault(); return; }
    if (drawerMode === 'protected') { renderDrawerListForProtectedWorkloads(); return; }
    if (drawerMode === 'unprotected-workload') { renderDrawerListForWorkloadTypes(); return; }
    if (drawerMode === 'source-workload') { renderDrawerListForWorkloadTypes(); return; }
    // [Phase 6] 'job' and 'job-workload' render dispatches removed.
    if (drawerMode === 'job-new-workload') { renderDrawerListForWorkloadTypes(); return; }

    const listEl = document.getElementById('gf-drawer-list');
    if (!listEl) return;

    const site = gfDrawerSiteId !== null ? getSiteById(gfDrawerSiteId) : null;
    // Sum the quantity (count/protectedTb) for each workload value on this site,
    // including both organised workloads and the uncategorised staging area.
    const addedCounts = {};
    if (site) {
        const allSiteWorkloads = [
            ...(Array.isArray(site.workloads) ? site.workloads : []),
            ...(Array.isArray(site.uncategorisedWorkloads) ? site.uncategorisedWorkloads : [])
        ];
        allSiteWorkloads.filter((w) => w.value).forEach((w) => {
            const qty = isProtectedTbWorkload(w.value)
                ? (w.protectedTb || 0)
                : (w.count || 0);
            addedCounts[w.value] = (addedCounts[w.value] || 0) + qty;
        });
    }

    const query = gfDrawerSearch;
    const activeFilters = gfDrawerActiveFilters;

    let html = '';
    let totalVisible = 0;

    workloadOptionGroups.forEach((group) => {
        if (activeFilters.size > 0 && !activeFilters.has(group.label)) return;

        const matchingOptions = group.options.filter((opt) => {
            if (!query) return true;
            return opt.label.toLowerCase().includes(query) || opt.value.toLowerCase().includes(query);
        });

        if (matchingOptions.length === 0) return;

        totalVisible += matchingOptions.length;

        const groupFallbackIcon = GF_SVG_ICONS[group.label] || GF_SVG_ICONS['Hypervisors'];
        const itemsHtml = matchingOptions.map((opt) => {
            const count = addedCounts[opt.value] || 0;
            const isAdded = count > 0;
            const itemIcon = getGfChipIcon(opt.value) || groupFallbackIcon;
            const isTb = isProtectedTbWorkload(opt.value);
            const badgeLabel = isTb ? `${count} TB` : `${count}`;
            return `<div class="gf-drawer-item${isAdded ? ' is-added' : ''}"
                        data-gf-add-wl="${escapeHtml(opt.value)}"
                        role="listitem"
                        title="Add ${escapeHtml(opt.label)} to this site">
                <span class="gf-drawer-item-icon">${itemIcon}</span>
                <span class="gf-drawer-item-label">${escapeHtml(opt.label)}</span>
                <span class="gf-drawer-item-check${isAdded ? '' : ' gf-drawer-item-check--zero'}">${badgeLabel}</span>
            </div>`;
        }).join('');

        html += `<div class="gf-drawer-group">
            <div class="gf-drawer-group-label">${escapeHtml(group.label)}</div>
            ${itemsHtml}
        </div>`;
    });

    if (totalVisible === 0) {
        html = `<div class="gf-drawer-empty">No workloads match your search.</div>`;
    }

    listEl.innerHTML = html;
}

/** Renders workload type options for 'unprotected-workload' and 'job-workload' drawer modes. */
function renderDrawerListForWorkloadTypes() {
    const listEl = document.getElementById('gf-drawer-list');
    if (!listEl) return;

    const site  = gfDrawerSiteId !== null ? getSiteById(gfDrawerSiteId) : null;
    const query = gfDrawerSearch.toLowerCase();

    // Show currently-selected type for 'job-workload' mode
    let selectedWorkloadType = null;
    if (drawerMode === 'job-workload' && gfDrawerContextId !== null && site) {
        const job = (site.jobs || []).find((j) => j.id === gfDrawerContextId);
        if (job) selectedWorkloadType = job.workloadType;
    } else if (drawerMode === 'unprotected-workload' && gfDrawerContextId !== null && site) {
        const row = (site.unprotectedWorkloads || []).find((w) => w.id === gfDrawerContextId);
        if (row) selectedWorkloadType = row.workloadType;
    }

    // Sum qty per workload type across all rows on this site
    const typeCounts = {};
    if (site) {
        if (drawerMode === 'unprotected-workload') {
            (site.unprotectedWorkloads || []).forEach((w) => {
                if (w.workloadType) {
                    typeCounts[w.workloadType] = (typeCounts[w.workloadType] || 0) + (w.count || 0);
                }
            });
        } else if (drawerMode === 'job-workload' || drawerMode === 'job-new-workload') {
            (site.jobs || []).forEach((j) => {
                if (j.workloadType) {
                    typeCounts[j.workloadType] = (typeCounts[j.workloadType] || 0) + (j.count || 0);
                }
            });
        }
    }

    // Determine if the workload picker is being used for a SureBackup job
    let pickerJobType = null;
    if (drawerMode === 'job-new-workload') {
        pickerJobType = gfDrawerPendingJobType;
    } else if (drawerMode === 'job-workload' && gfDrawerContextId !== null && site) {
        const job = (site.jobs || []).find((j) => j.id === gfDrawerContextId);
        if (job) pickerJobType = job.jobType;
    }
    const isSureBackupPicker = pickerJobType === 'surebackup';

    // Build grouped output
    const groups = [];
    const seenGroups = new Map();
    jobWorkloadOptions.forEach((w) => {
        if (isSureBackupPicker && !sureBackupAllowedWorkloads.has(w.value)) return;
        if (pickerJobType === 'replication' && !replicationAllowedWorkloads.has(w.value)) return;
        if (pickerJobType === 'cdp-replication' && !cdpAllowedWorkloads.has(w.value)) return;
        if (pickerJobType === 'backup-copy' && backupCopyExcludedWorkloads.has(w.value)) return;
        if (query && !w.label.toLowerCase().includes(query) && !w.value.toLowerCase().includes(query)) return;
        const grp = w.group || 'Other';
        if (drawerMode === 'unprotected-workload' && gfDrawerActiveFilters.size > 0 && !gfDrawerActiveFilters.has(grp)) return;
        if (!seenGroups.has(grp)) { seenGroups.set(grp, []); groups.push(grp); }
        seenGroups.get(grp).push(w);
    });

    if (groups.length === 0) {
        listEl.innerHTML = `<div class="gf-drawer-empty">No workload types match your search.</div>`;
        return;
    }

    listEl.innerHTML = groups.map((grpName) => {
        const fallbackIcon = GF_SVG_ICONS[grpName] || GF_SVG_ICONS['Hypervisors'];
        const itemsHtml = seenGroups.get(grpName).map((w) => {
            const isSelected = w.value === selectedWorkloadType;
            const count = typeCounts[w.value] || 0;
            const isAdded = count > 0;
            const icon = getGfChipIcon(w.value) || fallbackIcon;
            const fullPluginLabel = pluginFullLabels[w.value] || null;
            const pluginHint = fullPluginLabel
                ? `<span class="gf-drawer-plugin-hint" data-tooltip="${escapeHtml(fullPluginLabel)}" aria-label="${escapeHtml(fullPluginLabel)}">?</span>`
                : '';
            return `<div class="gf-drawer-item${isSelected ? ' is-added' : isAdded ? ' is-added' : ''}"
                         data-gf-add-wl="${escapeHtml(w.value)}"
                         role="listitem"
                         title="${escapeHtml(w.label)}">
                <span class="gf-drawer-item-icon">${icon}</span>
                <span class="gf-drawer-item-label">${escapeHtml(w.label)}</span>
                ${pluginHint}
                ${isAdded ? `<span class="gf-drawer-item-check">${count}</span>` : ''}
            </div>`;
        }).join('');
        return `<div class="gf-drawer-group">
            <div class="gf-drawer-group-label">${escapeHtml(grpName)}</div>
            ${itemsHtml}
        </div>`;
    }).join('');
}

/** Renders job type options for the 'job' drawer mode. */
function renderDrawerListForJobTypes() {
    const listEl = document.getElementById('gf-drawer-list');
    if (!listEl) return;

    const site  = gfDrawerSiteId !== null ? getSiteById(gfDrawerSiteId) : null;
    const query = gfDrawerSearch.toLowerCase();

    // Show currently-selected type when changing an existing job's type
    let selectedJobType = null;
    let jobWorkloadType = null;
    if (gfDrawerContextId !== null && site) {
        const job = (site.jobs || []).find((j) => j.id === gfDrawerContextId);
        if (job) {
            selectedJobType  = job.jobType;
            jobWorkloadType  = job.workloadType || null;
        }
    }

    // Returns true when a job type is compatible with the job's current workload type.
    // Only applied when editing an existing job (jobWorkloadType is set).
    const isCompatible = (jobTypeValue) => {
        if (!jobWorkloadType) return true;
        switch (jobTypeValue) {
            case 'backup':          return true;
            case 'backup-copy':     return !backupCopyExcludedWorkloads.has(jobWorkloadType);
            case 'replication':     return replicationAllowedWorkloads.has(jobWorkloadType);
            case 'cdp-replication': return cdpAllowedWorkloads.has(jobWorkloadType);
            case 'surebackup':      return sureBackupAllowedWorkloads.has(jobWorkloadType);
            default:                return true;
        }
    };

    const workloadLabel = jobWorkloadType
        ? (jobWorkloadOptions.find((w) => w.value === jobWorkloadType)?.label ?? jobWorkloadType)
        : null;

    const items = jobTypeOptions.filter((t) => !query || t.label.toLowerCase().includes(query));

    // Count existing jobs of each type on this site (for the count badge)
    const jobTypeCounts = {};
    if (site) {
        (site.jobs || []).forEach((j) => {
            jobTypeCounts[j.jobType] = (jobTypeCounts[j.jobType] || 0) + 1;
        });
    }

    const vbrPresent = hasVbrAcrossAllSites();
    const lockedNotice = !vbrPresent
        ? `<div class="gf-drawer-locked-notice">A VBR server is required before jobs can be added. Use Add Veeam Component first.</div>`
        : '';
    const lockSvg = `<span class="gf-drawer-item-lock" aria-hidden="true"><svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg></span>`;
    listEl.innerHTML = lockedNotice + items.map((t) => {
        const isSelected   = t.value === selectedJobType;
        const noVbr        = !vbrPresent;
        const incompatible = !isCompatible(t.value);
        const isLocked     = noVbr || incompatible;
        const count        = jobTypeCounts[t.value] || 0;
        const showBadge    = count > 0 && !isSelected;
        const lockTitle    = noVbr
            ? 'Add a VBR server first'
            : `Not supported for ${workloadLabel} workloads`;
        return `<div class="gf-drawer-item${isSelected ? ' is-added' : ''}${isLocked ? ' is-locked' : ''}"
                     data-gf-add-wl="${escapeHtml(t.value)}"
                     role="listitem"
                     title="${isLocked ? escapeHtml(lockTitle) : escapeHtml(t.label)}">
            <span class="gf-drawer-item-icon">${GF_SVG_DOC}</span>
            <span class="gf-drawer-item-label">${escapeHtml(t.label)}</span>
            ${isLocked ? lockSvg : isSelected ? `<span class="gf-drawer-item-check">✓</span>` : showBadge ? `<span class="gf-drawer-item-check">${count}</span>` : ''}
        </div>`;
    }).join('');
}

/**
 * Count badge + inline minus control for an added "Add Veeam Component" drawer row.
 * Resting state shows only the count badge (identical to unadded rows); the minus is a
 * subtle borderless circle that brightens on row hover and turns red on direct hover.
 * `isServer` routes the click to a named-server vs count-chip removal in the click handler.
 */
function buildDrawerStepperBadge(count, removeValue, isServer, label) {
    const routeAttr = isServer ? ' data-gf-remove-server="1"' : ' data-gf-remove-comp="1"';
    return `<span class="gf-drawer-item-stepper">`
        + `<button type="button" class="gf-drawer-item-minus" data-gf-remove-wl="${escapeHtml(removeValue)}"${routeAttr} aria-label="Remove one ${escapeHtml(label)}" title="Remove one ${escapeHtml(label)}">&minus;</button>`
        + `<span class="gf-drawer-item-check">${count}</span>`
        + `</span>`;
}

/**
 * Combined drawer for "Add Veeam Component":
 * — Top section: named server instances (VBR, VB365, Kasten, etc.) → create named card on this site
 * — Lower sections: per-site infrastructure/jobs chips (proxies, repos, WAN, agents, plug-ins, jobs)
 *   that can exist on ANY site regardless of where the VBR lives.
 */
function renderDrawerListForServerTypes() {
    const listEl = document.getElementById('gf-drawer-list');
    if (!listEl) return;

    const query         = gfDrawerSearch.toLowerCase();
    const site          = gfDrawerSiteId !== null ? getSiteById(gfDrawerSiteId) : null;
    const existingServers = Array.isArray(site?.veeamServers) ? site.veeamServers : [];
    const rawEnv        = site?.existingEnvironment || {};
    const activeFilters = gfDrawerActiveFilters;

    const serverTypeIconSvg = window.VEEAM_SERVER_TYPE_ICONS;
    const VEEAM_SERVER_TYPES = window.VEEAM_SERVER_TYPES;

    let html = '';
    let totalVisible = 0;

    // ── VDP: named badge types + count-chip server types in one group ─
    const serverSection = 'VDP';
    if (activeFilters.size === 0 || activeFilters.has(serverSection)) {
        // Named badge items — VBR/VBEM/VONE are count chips; VBfAWS/VBfAz/VBfGC moved to VBfCloud;
        // VB365 moved to VB365 section; Kasten moved to Containers section
        const matchingBadgeTypes = Object.entries(VEEAM_SERVER_TYPES).filter(([typeKey, typeDef]) =>
            typeKey !== 'vbr' && typeKey !== 'vbfaws' && typeKey !== 'vbfaz' && typeKey !== 'vbfgc' &&
            typeKey !== 'vbem' && typeKey !== 'vone' && typeKey !== 'vb365' && typeKey !== 'kasten' &&
            (!query || typeDef.label.toLowerCase().includes(query) || typeDef.fullLabel.toLowerCase().includes(query))
        );
        // Count-chip server items (VBR, VRO, VONE, VBEM)
        const countChipServerKeys = ['vbrServers', 'vbemServers', 'veeamOneServers', 'vroServers'];
        const matchingCountServers = countChipServerKeys
            .map((key) => existingEnvironmentFieldSchemaByKey.get(key))
            .filter((def) => def && (!query || (existingEnvironmentFieldLabels[def.key] || def.key).toLowerCase().includes(query)));

        const totalGroupItems = matchingBadgeTypes.length + matchingCountServers.length;
        if (totalGroupItems > 0) {
            totalVisible += totalGroupItems;

            const buildBadgeItem = ([typeKey, typeDef]) => {
                const count   = existingServers.filter((s) => s.type === typeKey).length;
                const iconSvg = serverTypeIconSvg[typeKey] || serverTypeIconSvg.vbr;
                const versions = typeDef.versions;
                const selectedVer = drawerServerVersions[typeKey];
                const versionToggle = versions
                    ? `<div class="gf-drawer-vbr-version" role="group" aria-label="Version of ${escapeHtml(typeDef.label)}" data-gf-version-toggle data-gf-version-type="${escapeHtml(typeKey)}">
                        ${versions.map((v) =>
                            `<label class="gf-drawer-version-opt"><input type="radio" name="drawer-server-version-${escapeHtml(typeKey)}" value="${v}"${selectedVer === v ? ' checked' : ''}><span>${v}</span></label>`
                        ).join('')}
                       </div>`
                    : '';
                return `<div class="gf-drawer-item${count > 0 ? ' is-added' : ''}"
                             data-gf-add-wl="${escapeHtml(typeKey)}" data-gf-add-server="1"
                             role="listitem" title="Add ${escapeHtml(typeDef.fullLabel)} instance">
                    <span class="gf-drawer-item-icon">${iconSvg}</span>
                    <span class="gf-drawer-item-label">${escapeHtml(typeDef.label)}</span>
                    ${versionToggle}
                    ${count > 0 ? buildDrawerStepperBadge(count, typeKey, true, typeDef.label) : ''}
                </div>`;
            };

            const buildCountItem = (def) => {
                const siteCount = normalizeEnvironmentCount(rawEnv[def.key]);
                const iconSvg   = serverTypeIconSvg[def.key] || serverTypeIconSvg.vbr;
                const label     = existingEnvironmentFieldLabels[def.key] || def.key;
                const vbrIsV13 = drawerSourceVersion === 'v13';
                const vbrVersionToggle = (def.key === 'vbrServers')
                    ? `<div class="gf-drawer-vbr-toggles" data-gf-version-toggle>
                        <div class="gf-drawer-vbr-version" role="group" aria-label="VBR platform">
                            ${vbrIsV13
                                ? `<label class="gf-drawer-version-opt" title="Windows"><input type="radio" name="drawer-vbr-platform" value="windows"${drawerVbrPlatform === 'windows' ? ' checked' : ''}><span><svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style="display:block"><path d="M3 5.56L10.53 4.5v7.27H3zm8.4-1.19L21 3v8.77h-9.6zm-8.4 7.87h7.53v7.15L3 18.44zm8.4.11H21V21l-9.6-1.37z"/></svg></span></label><label class="gf-drawer-version-opt" title="Linux (VSA)"><input type="radio" name="drawer-vbr-platform" value="linux"${drawerVbrPlatform === 'linux' ? ' checked' : ''}><span><img src="https://cdn.jsdelivr.net/npm/simple-icons@v13/icons/linux.svg" width="15" height="15" class="os-icon" alt="Linux"></span></label>`
                                : `<span class="gf-drawer-version-opt gf-drawer-version-opt--locked" aria-label="Windows (only option for this version)"><span><svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style="display:block"><path d="M3 5.56L10.53 4.5v7.27H3zm8.4-1.19L21 3v8.77h-9.6zm-8.4 7.87h7.53v7.15L3 18.44zm8.4.11H21V21l-9.6-1.37z"/></svg></span></span>`
                            }
                        </div>
                        <div class="gf-drawer-vbr-version" role="group" aria-label="Source VBR version">
                            ${['v11', 'v12', 'v13'].map((v) =>
                                `<label class="gf-drawer-version-opt"><input type="radio" name="drawer-source-version" value="${v}"${drawerSourceVersion === v ? ' checked' : ''}><span>${v}</span></label>`
                            ).join('')}
                        </div>
                       </div>`
                    : (def.key === 'vroServers')
                    ? `<div class="gf-drawer-vbr-version" role="group" aria-label="Source VRO version" data-gf-version-toggle>
                        ${['v11', 'v12', 'v13'].map((v) =>
                            `<label class="gf-drawer-version-opt"><input type="radio" name="drawer-vro-source-version" value="${v}"${drawerVroSourceVersion === v ? ' checked' : ''}><span>${v}</span></label>`
                        ).join('')}
                       </div>`
                    : (def.key === 'veeamOneServers')
                    ? `<div class="gf-drawer-vbr-version" role="group" aria-label="Source VONE version" data-gf-version-toggle>
                        ${['v11', 'v12', 'v13'].map((v) =>
                            `<label class="gf-drawer-version-opt"><input type="radio" name="drawer-vone-source-version" value="${v}"${drawerVoneSourceVersion === v ? ' checked' : ''}><span>${v}</span></label>`
                        ).join('')}
                       </div>`
                    : '';
                // VBR gets a wrapper so the VSA HA expandable section can sit outside the click target
                if (def.key === 'vbrServers') {
                    const vsaHaSection = (vbrIsV13 && drawerVbrPlatform === 'linux')
                        ? `<div class="gf-drawer-vbr-extra">
                            <span class="gf-drawer-vbr-extra-label">VSA HA</span>
                            <div class="gf-drawer-vbr-version" role="group" aria-label="VSA HA" data-gf-version-toggle>
                                <label class="gf-drawer-version-opt"><input type="radio" name="drawer-vbr-vsa-ha" value="no"${drawerVbrVsaHa === 'no' ? ' checked' : ''}><span>No</span></label><label class="gf-drawer-version-opt"><input type="radio" name="drawer-vbr-vsa-ha" value="yes"${drawerVbrVsaHa === 'yes' ? ' checked' : ''}><span>Yes</span></label>
                            </div>
                          </div>`
                        : '';
                    return `<div class="gf-drawer-vbr-wrapper${siteCount > 0 ? ' is-added' : ''}">
                        <div class="gf-drawer-item${siteCount > 0 ? ' is-added' : ''}"
                             data-gf-add-wl="${escapeHtml(def.key)}" data-gf-add-comp="1"
                             role="listitem" title="${escapeHtml(siteCount > 0 ? `${label} — ${siteCount} on this site` : `Add ${label}`)}">
                            <span class="gf-drawer-item-icon">${iconSvg}</span>
                            <span class="gf-drawer-item-label">${escapeHtml(label)}</span>
                            ${vbrVersionToggle}
                            ${siteCount > 0 ? buildDrawerStepperBadge(siteCount, def.key, false, label) : ''}
                        </div>
                        ${vsaHaSection}
                    </div>`;
                }
                return `<div class="gf-drawer-item${siteCount > 0 ? ' is-added' : ''}"
                             data-gf-add-wl="${escapeHtml(def.key)}" data-gf-add-comp="1"
                             role="listitem" title="${escapeHtml(siteCount > 0 ? `${label} — ${siteCount} on this site` : `Add ${label}`)}">
                    <span class="gf-drawer-item-icon">${iconSvg}</span>
                    <span class="gf-drawer-item-label">${escapeHtml(label)}</span>
                    ${vbrVersionToggle}
                    ${siteCount > 0 ? buildDrawerStepperBadge(siteCount, def.key, false, label) : ''}
                </div>`;
            };

            // Order: count chips (VBR first, then VRO/VONE/VBEM) · remaining named badge types
            const itemsHtml = [
                ...matchingCountServers.map(buildCountItem),
                ...matchingBadgeTypes.map(buildBadgeItem),
            ].join('');

            html += `<div class="gf-drawer-group">
                <div class="gf-drawer-group-label">${escapeHtml(serverSection)}</div>
                ${itemsHtml}
            </div>`;
        }
    }

    // ── Sections 2+: per-site infrastructure/jobs chips ───────────────────────
    const vbrPresent   = hasVbrAcrossAllSites();
    const vb365Present = sites.some((s) =>
        Array.isArray(s.veeamServers) && s.veeamServers.some((sv) => sv.type === 'vb365')
    );

    // Shared builder for named server badge items (VB365, Kasten, VBfAWS/Az/GC)
    const buildServerBadgeItemHtml = ([typeKey, typeDef]) => {
            const count      = existingServers.filter((s) => s.type === typeKey).length;
            const badgeIconSvg = serverTypeIconSvg[typeKey] || serverTypeIconSvg.vbr;
            const versions   = typeDef.versions;
            const selectedVer = drawerServerVersions[typeKey];
            const versionToggle = versions
                ? `<div class="gf-drawer-vbr-version" role="group" aria-label="Version of ${escapeHtml(typeDef.label)}" data-gf-version-toggle>
                    ${versions.map((v) =>
                        `<label class="gf-drawer-version-opt"><input type="radio" name="drawer-server-version-${escapeHtml(typeKey)}" value="${v}"${selectedVer === v ? ' checked' : ''}><span>${v}</span></label>`
                    ).join('')}
                   </div>`
                : '';
            return `<div class="gf-drawer-item${count > 0 ? ' is-added' : ''}"
                         data-gf-add-wl="${escapeHtml(typeKey)}" data-gf-add-server="1"
                         role="listitem" title="Add ${escapeHtml(typeDef.fullLabel)} instance">
                <span class="gf-drawer-item-icon">${badgeIconSvg}</span>
                <span class="gf-drawer-item-label">${escapeHtml(typeDef.label)}</span>
                ${versionToggle}
                ${count > 0 ? buildDrawerStepperBadge(count, typeKey, true, typeDef.label) : ''}
            </div>`;
    };

    existingEnvironmentDrawerGroups.forEach((group) => {
        if (activeFilters.size > 0 && !activeFilters.has(group.label)) return;

        const fields = group.keys
            .map((key) => existingEnvironmentFieldSchemaByKey.get(key))
            .filter((def) => def && isExistingEnvironmentFieldAvailableForEngagement(def))
            .filter((def) => !query || (existingEnvironmentFieldLabels[def.key] || def.key).toLowerCase().includes(query));

        // For VBfCloud: also render VBfAWS / VBfAz / VBfGC named-server badges above the count chips
        const vbfCloudBadgeTypes = (group.label === 'VBfCloud')
            ? Object.entries(VEEAM_SERVER_TYPES).filter(([typeKey, typeDef]) =>
                ['vbfaws', 'vbfaz', 'vbfgc'].includes(typeKey) &&
                (!query || typeDef.label.toLowerCase().includes(query) || typeDef.fullLabel.toLowerCase().includes(query))
              )
            : [];

        // For VB365: also render the VB365 named-server badge above the infra count chips
        const vb365BadgeTypes = (group.label === 'VB365')
            ? Object.entries(VEEAM_SERVER_TYPES).filter(([typeKey, typeDef]) =>
                typeKey === 'vb365' &&
                (!query || typeDef.label.toLowerCase().includes(query) || typeDef.fullLabel.toLowerCase().includes(query))
              )
            : [];

        if (!fields.length && !vbfCloudBadgeTypes.length && !vb365BadgeTypes.length) return;
        totalVisible += fields.length + vbfCloudBadgeTypes.length + vb365BadgeTypes.length;

        const iconSvg = EE_DRAWER_SVG_ICONS[group.label] || EE_DRAWER_SVG_ICONS['VBR Backup Infrastructure'];

        const vb365BadgesHtml    = vb365BadgeTypes.map(buildServerBadgeItemHtml).join('');

        const vbfCloudBadgesHtml = vbfCloudBadgeTypes.map(buildServerBadgeItemHtml).join('');

        const itemsHtml = fields.map((def) => {
            // proxyServers is a virtual trigger key — the real counts live in the typed fields.
            // repositoryServers (VBR repos) are stored as workloads (isRepositoryComponent),
            // not as an existingEnvironment count, so derive the count from site.workloads.
            const repoComponentCount = Array.isArray(site?.workloads)
                ? site.workloads.filter((w) => w.isRepositoryComponent).length
                : 0;
            const siteCount = def.key === 'proxyServers'
                ? (normalizeEnvironmentCount(rawEnv.proxyServersWindows) + normalizeEnvironmentCount(rawEnv.proxyServersLinux))
                : def.key === 'repositoryServers'
                ? (repoComponentCount || normalizeEnvironmentCount(rawEnv.repositoryServers))
                : normalizeEnvironmentCount(rawEnv[def.key]);
            const isAdded    = siteCount > 0;
            const isLockedVbr   = existingEnvironmentGlobalVbrDependentKeys.has(def.key) && !vbrPresent;
            const isLockedVb365 = existingEnvironmentGlobalVb365DependentKeys.has(def.key) && !vb365Present;
            const isLocked      = isLockedVbr || isLockedVb365;
            const label      = existingEnvironmentFieldLabels[def.key] || def.key;
            const titleText  = isLockedVbr   ? `${label} — requires a VBR server (any site)`
                             : isLockedVb365 ? `${label} — requires a VB365 server (any site)`
                             : isAdded       ? `${label} — ${siteCount} on this site`
                             :                 `Add ${label}`;
            // Win/Linux platform toggle for repository, VB365 proxy rows (per-site state)
            // and the proxy row (module-level pendingProxyOsType — captured into proxyDraft on click)
            const platformField   = CHIP_PLATFORM_FIELD[def.key];
            const isProxyRow      = def.key === 'proxyServers';
            const effectivePlatform = platformField ? (site?.[platformField] || 'windows')
                                    : isProxyRow    ? pendingProxyOsType
                                    : null;
            const platformToggle = ((platformField || isProxyRow) && !isLocked)
                ? `<div class="gf-drawer-vbr-version" role="group" aria-label="${escapeHtml(label)} platform" data-gf-version-toggle>
                        <label class="gf-drawer-version-opt" title="Windows"><input type="radio" name="drawer-comp-platform-${escapeHtml(def.key)}" value="windows" ${platformField ? `data-site-id="${gfDrawerSiteId}" data-ee-drawer-platform="${escapeHtml(platformField)}"` : 'data-gf-proxy-os-toggle'}${effectivePlatform !== 'linux' ? ' checked' : ''}><span><svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style="display:block"><path d="M3 5.56L10.53 4.5v7.27H3zm8.4-1.19L21 3v8.77h-9.6zm-8.4 7.87h7.53v7.15L3 18.44zm8.4.11H21V21l-9.6-1.37z"/></svg></span></label><label class="gf-drawer-version-opt" title="Linux"><input type="radio" name="drawer-comp-platform-${escapeHtml(def.key)}" value="linux" ${platformField ? `data-site-id="${gfDrawerSiteId}" data-ee-drawer-platform="${escapeHtml(platformField)}"` : 'data-gf-proxy-os-toggle'}${effectivePlatform === 'linux' ? ' checked' : ''}><span><img src="https://cdn.jsdelivr.net/npm/simple-icons@v13/icons/linux.svg" width="15" height="15" class="os-icon" alt="Linux"></span></label>
                   </div>`
                : '';
            return `<div class="gf-drawer-item${isAdded ? ' is-added' : ''}${isLocked ? ' is-locked' : ''}"
                        data-gf-add-wl="${escapeHtml(def.key)}" data-gf-add-comp="1"
                        role="listitem" title="${escapeHtml(titleText)}">
                <span class="gf-drawer-item-icon">${iconSvg}</span>
                <span class="gf-drawer-item-label">${escapeHtml(label)}</span>
                ${platformToggle}
                ${isAdded ? buildDrawerStepperBadge(siteCount, def.key, false, label) : ''}
                ${isLocked ? `<span class="gf-drawer-item-lock" aria-hidden="true"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>` : ''}
            </div>`;
        }).join('');

        html += `<div class="gf-drawer-group">
            <div class="gf-drawer-group-label">${escapeHtml(group.label)}</div>
            ${vb365BadgesHtml}
            ${vbfCloudBadgesHtml}
            ${itemsHtml}
        </div>`;
    });

    // ── Containers section (Kasten K10) ──────────────────────────────────────
    if (activeFilters.size === 0 || activeFilters.has('Containers')) {
        const containerBadgeTypes = Object.entries(VEEAM_SERVER_TYPES).filter(([typeKey, typeDef]) =>
            typeKey === 'kasten' &&
            (!query || typeDef.label.toLowerCase().includes(query) || typeDef.fullLabel.toLowerCase().includes(query))
        );
        if (containerBadgeTypes.length > 0) {
            totalVisible += containerBadgeTypes.length;
            const containersHtml = containerBadgeTypes.map(buildServerBadgeItemHtml).join('');
            html += `<div class="gf-drawer-group">
                <div class="gf-drawer-group-label">Containers</div>
                ${containersHtml}
            </div>`;
        }
    }

    if (totalVisible === 0) {
        html = `<div class="gf-drawer-empty">No components match your search.</div>`;
    }
    listEl.innerHTML = html;
}

/** Drawer for "Add Protected Workload" — same workload list but in protected mode */
function renderDrawerListForProtectedWorkloads() {
    const listEl = document.getElementById('gf-drawer-list');
    if (!listEl) return;

    const site = gfDrawerSiteId !== null ? getSiteById(gfDrawerSiteId) : null;
    const protectedWl = Array.isArray(site?.protectedWorkloads) ? site.protectedWorkloads : [];
    const addedCounts = {};
    protectedWl.filter((w) => w.value).forEach((w) => {
        const qty = isProtectedTbWorkload(w.value) ? (w.protectedTb || 0) : (w.count || 0);
        addedCounts[w.value] = (addedCounts[w.value] || 0) + qty;
    });

    const query = gfDrawerSearch;
    const activeFilters = gfDrawerActiveFilters;
    let html = '';
    let totalVisible = 0;

    workloadOptionGroups.forEach((group) => {
        if (activeFilters.size > 0 && !activeFilters.has(group.label)) return;
        const matchingOptions = group.options.filter((opt) => {
            if (!query) return true;
            return opt.label.toLowerCase().includes(query) || opt.value.toLowerCase().includes(query);
        });
        if (!matchingOptions.length) return;
        totalVisible += matchingOptions.length;
        const groupFallbackIcon = GF_SVG_ICONS[group.label] || GF_SVG_ICONS['Hypervisors'];
        const itemsHtml = matchingOptions.map((opt) => {
            const count = addedCounts[opt.value] || 0;
            const isAdded = count > 0;
            const itemIcon = getGfChipIcon(opt.value) || groupFallbackIcon;
            return `<div class="gf-drawer-item${isAdded ? ' is-added' : ''}"
                        data-gf-add-wl="${escapeHtml(opt.value)}" role="listitem"
                        title="Add ${escapeHtml(opt.label)} as protected workload">
                <span class="gf-drawer-item-icon">${itemIcon}</span>
                <span class="gf-drawer-item-label">${escapeHtml(opt.label)}</span>
                ${isAdded ? `<span class="gf-drawer-item-check">${count}</span>` : ''}
            </div>`;
        }).join('');
        html += `<div class="gf-drawer-group">
            <div class="gf-drawer-group-label">${escapeHtml(group.label)}</div>
            ${itemsHtml}
        </div>`;
    });

    if (totalVisible === 0) {
        html = `<div class="gf-drawer-empty">No workloads match your search.</div>`;
    }
    listEl.innerHTML = html;
}

function renderDrawerListForVault() {
    const listEl = document.getElementById('gf-drawer-list');
    if (!listEl) return;

    const query = gfDrawerSearch;
    const options = repoMigrationSourceStorageOptions
        .filter((o) => o.value)
        .filter((o) => !query || o.label.toLowerCase().includes(query.toLowerCase()));

    if (!options.length) {
        listEl.innerHTML = `<div class="gf-drawer-empty">No repository types match your search.</div>`;
        return;
    }

    const itemsHtml = options.map((opt) => {
        const iconSvg = VAULT_DRAWER_ICONS[opt.value] || VAULT_DRAWER_ICONS['default'];
        const globalCount = sites.reduce((sum, s) =>
            sum + getRepoMigrationWorkloads(s).filter((w) => w.vdcVaultSourceStorageType === opt.value).length, 0);
        const titleText = globalCount > 0
            ? `${escapeHtml(opt.label)} — ${globalCount} across all sites`
            : `Add ${escapeHtml(opt.label)}`;
        return `
        <div class="gf-drawer-item${globalCount > 0 ? ' is-added' : ''}"
             data-gf-add-wl="${escapeHtml(opt.value)}"
             role="listitem"
             title="${titleText}">
            <span class="gf-drawer-item-icon">${iconSvg}</span>
            <span class="gf-drawer-item-label">${escapeHtml(opt.label)}</span>
            ${globalCount > 0 ? `<span class="gf-drawer-item-check">${globalCount}</span>` : ''}
        </div>`;
    }).join('');

    listEl.innerHTML = `<div class="gf-drawer-group">
        <div class="gf-drawer-group-label">Source Storage Type</div>
        ${itemsHtml}
    </div>`;
}

/** Build per-site category pills HTML — mirrors summary bar icons/vocab; zero-count hidden */
function buildSitePillsHtml(unprotectedWl, jobs, vaultWl, namedServers, compItems) {
    const componentCount = namedServers.length + compItems.length;
    const svgIcon = (path) => `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`;
    const pill    = (n, sing, plur, svgPath) => n === 0 ? '' :
        `<span class="gf-site-pill">${svgIcon(svgPath)}<strong>${n}</strong> ${n === 1 ? sing : plur}</span>`;
    return [
        pill(unprotectedWl.length, 'unprotected', 'unprotected',
            '<path d="M12 3L20 7v5c0 5.25-3.6 10.2-8 11.95C7.6 22.2 4 17.25 4 12V7l8-4z"/><path d="M12 10v3M12 16.5h.01"/>'),
        pill(jobs.length, 'job', 'jobs',
            '<rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>'),
        pill(vaultWl.length, 'repo', 'repos',
            '<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v4c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/><path d="M3 9v6c0 1.66 4.03 3 9 3s9-1.34 9-3V9"/>'),
        pill(componentCount, 'component', 'components',
            '<rect x="3" y="5" width="18" height="6" rx="1.5"/><rect x="3" y="13" width="18" height="6" rx="1.5"/><path d="M7 8h.01M7 16h.01"/>'),
    ].filter(Boolean).join('');
}

// refreshGfSiteChips removed in Phase 6 — callers now emit BUS_EVENTS.SITE_LIST_CHANGED
// which triggers a full renderSiteList(). The partial DOM update was only needed when
// jobs/unprotected workloads mutated without a full re-render; those paths are gone.

/**
 * Add a Veeam component to a site from the drawer picker.
 * Handles toggle (enable) and count (set to ≥1) fields.
 * If the field depends on VBR, VBR is also enabled automatically.
 */
function addComponentToSiteFromDrawer(siteId, fieldKey) {
    const site = getSiteById(siteId);
    if (!site) return;

    // Proxies: OS type is selected via the inline toggle in this drawer.
    // Add directly using pendingProxyOsType — no second drawer needed.
    if (fieldKey === 'proxyServers') {
        if (!site.existingEnvironment || typeof site.existingEnvironment !== 'object' || Array.isArray(site.existingEnvironment)) {
            site.existingEnvironment = normalizeExistingEnvironment({});
        }
        const proxyField = pendingProxyOsType === 'linux' ? 'proxyServersLinux' : 'proxyServersWindows';
        const currentProxyCount = normalizeEnvironmentCount(site.existingEnvironment[proxyField]);
        site.existingEnvironment[proxyField] = currentProxyCount + 1;
        if (!Array.isArray(site.existingEnvironmentDisplayOrder)) site.existingEnvironmentDisplayOrder = [];
        if (!site.existingEnvironmentDisplayOrder.includes(proxyField)) site.existingEnvironmentDisplayOrder.push(proxyField);
        renderGfDrawerList();
        emit(BUS_EVENTS.SITE_LIST_CHANGED);
        window.updateTotalInstances?.();
        window.updateWorkloadValidationState?.();
        window.updateGfSummaryCount?.();
        return;
    }

    // Repositories use the vault-style 2-step drawer (source storage type → repo type).
    if (fieldKey === 'repositoryServers') {
        closeGfDrawer();
        openVaultStepDrawer(siteId, 'component');
        return;
    }

    // VB365 repositories use a single-step storage type picker drawer.
    if (fieldKey === 'vb365RepositoryServers') {
        closeGfDrawer();
        openVb365RepoStepDrawer(siteId, 'add');
        return;
    }

    const definition = existingEnvironmentFieldSchemaByKey.get(fieldKey);
    if (!definition) return;

    if (!site.existingEnvironment || typeof site.existingEnvironment !== 'object' || Array.isArray(site.existingEnvironment)) {
        site.existingEnvironment = normalizeExistingEnvironment({});
    }

    // Enable any required parent (e.g. VBR for proxy/repo/jobs)
    const parentField = definition.dependsOn;
    if (parentField) {
        const parentWasAbsent = normalizeEnvironmentCount(site.existingEnvironment[parentField]) === 0;
        site.existingEnvironment[parentField] = 'yes';
        if (parentField === 'vbrServers') {
            window.applyDefaultVbrChildRows(site.existingEnvironment);
        }
        if (parentWasAbsent) {
            if (!Array.isArray(site.existingEnvironmentDisplayOrder)) site.existingEnvironmentDisplayOrder = [];
            if (!site.existingEnvironmentDisplayOrder.includes(parentField)) {
                site.existingEnvironmentDisplayOrder.push(parentField);
            }
        }
    }

    // Add the field itself — always increment count by 1
    const current = normalizeEnvironmentCount(site.existingEnvironment[fieldKey]);
    const isNew = current === 0;
    site.existingEnvironment[fieldKey] = current > 0 ? current + 1 : 1;

    // For VBR / VRO / VONE, tag the new slot with the drawer's selected source version
    if (fieldKey === 'vbrServers') {
        if (!site.existingEnvironmentVbrVersions || typeof site.existingEnvironmentVbrVersions !== 'object') {
            site.existingEnvironmentVbrVersions = {};
        }
        site.existingEnvironmentVbrVersions[current] = drawerSourceVersion;
        if (!site.existingEnvironmentVbrPlatforms || typeof site.existingEnvironmentVbrPlatforms !== 'object') {
            site.existingEnvironmentVbrPlatforms = {};
        }
        site.existingEnvironmentVbrPlatforms[current] = drawerSourceVersion !== 'v13' ? 'windows' : drawerVbrPlatform;
    }
    if (fieldKey === 'vroServers') {
        if (!site.existingEnvironmentVroVersions || typeof site.existingEnvironmentVroVersions !== 'object') {
            site.existingEnvironmentVroVersions = {};
        }
        site.existingEnvironmentVroVersions[current] = drawerVroSourceVersion;
    }
    if (fieldKey === 'veeamOneServers') {
        if (!site.existingEnvironmentVoneVersions || typeof site.existingEnvironmentVoneVersions !== 'object') {
            site.existingEnvironmentVoneVersions = {};
        }
        site.existingEnvironmentVoneVersions[current] = drawerVoneSourceVersion;
    }

    // Track insertion order
    if (isNew) {
        if (!Array.isArray(site.existingEnvironmentDisplayOrder)) site.existingEnvironmentDisplayOrder = [];
        if (!site.existingEnvironmentDisplayOrder.includes(fieldKey)) {
            site.existingEnvironmentDisplayOrder.push(fieldKey);
        }
    }

    // Use 'upgrade' so all component field types are preserved.
    site.existingEnvironment = window.pruneExistingEnvironmentState(site.existingEnvironment, 'upgrade');
    site.pendingExistingEnvironmentSelection = false;

    emit(BUS_EVENTS.SITE_LIST_CHANGED);
    window.syncDesignOptionFields?.();
    window.updateNonGfNameBarCount?.();
    window.updateGfSummaryCount?.();
}

/** Add a vault migration chip to a site, pre-populating source, repo and target vault */
function addRepoMigrationRowToSite(siteId, sourceStorageType = '', repositoryType = '', targetVault = '', isRepositoryComponent = false) {
    const site = getSiteById(siteId);
    if (!site) return false;
    const entry = window.buildRepoMigrationEntry(50);
    if (sourceStorageType)    entry.vdcVaultSourceStorageType = sourceStorageType;
    if (repositoryType)       entry.vdcVaultRepositoryType    = repositoryType;
    if (targetVault)          entry.vdcVaultTargetVault       = targetVault;
    if (isRepositoryComponent) entry.isRepositoryComponent    = true;
    site.workloads.push(entry);
    emit(BUS_EVENTS.SITE_LIST_CHANGED);
    return true;
}
